import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CatalogField from "./CatalogField";
import { PRIORITIES, STATUSES, STATUS_ORDER, PRIORITY_ORDER, CHECKLIST_LABELS, fmtDate } from "../lib/constants";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Bug, Trash2, Plus, Mail, ListTodo, Pencil, Check, X, Paperclip, Upload, Download, Eye, Loader2 } from "lucide-react";
import { API } from "../lib/apiClient";

const EMPTY = {
  title: "", description: "", impacto: "", sistema: "", modulo: "", submodulo: "", folio: "", departamento_solicitante: "", solicitante: "", responsables: [], priority: "media", status: "pendiente",
  fecha_solicitud: "", fecha_compromiso: "", horas_estimadas: 0, horas_consumidas: 0,
};

export default function DevDialog({ open, onOpenChange, dev, users, allDevs, onSaved, catalog, onCatalogChange, departamentoOptions = [], solicitanteOptions = [] }) {
  const { isPM, user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [checklist, setChecklist] = useState({});
  const [correcciones, setCorrecciones] = useState([]);
  const [draftCorr, setDraftCorr] = useState("");
  const [subtareas, setSubtareas] = useState([]);
  const [draftSub, setDraftSub] = useState({ titulo: "", descripcion: "" });
  const [editSub, setEditSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(dev);
  const showCorrecciones = isEdit && ["en_pruebas", "correccion"].includes(form.status);

  const completadas = subtareas.filter((s) => s.completada).length;
  const subAvance = subtareas.length ? Math.round((completadas / subtareas.length) * 100) : null;
  const canManageSub = isPM || user?.role === "qa";
  const isResponsable = (form.responsables || []).includes(user?.id);
  const canToggleSub = ["pm", "dev", "qa"].includes(user?.role) || isResponsable;

  const sistemas = catalog?.sistemas || [];
  const sistemaObj = sistemas.find((s) => s.name === form.sistema);
  const moduloOptions = (sistemaObj?.modulos || []).map((m) => m.name);
  const moduloObj = (sistemaObj?.modulos || []).find((m) => m.name === form.modulo);
  const submoduloOptions = moduloObj?.submodulos || [];

  useEffect(() => {
    if (dev) {
      setForm({
        title: dev.title || "", description: dev.description || "", impacto: dev.impacto || "",
        sistema: dev.sistema || "", modulo: dev.modulo || "", submodulo: dev.submodulo || "",
        folio: dev.folio || "",
        departamento_solicitante: dev.departamento_solicitante || "", solicitante: dev.solicitante || "",
        responsables: dev.responsables_ids || (dev.responsables || []).map((r) => r.id) || [], priority: dev.priority || "media",
        status: dev.status || "pendiente",
        fecha_solicitud: dev.fecha_solicitud ? dev.fecha_solicitud.slice(0, 10) : "",
        fecha_compromiso: dev.fecha_compromiso ? dev.fecha_compromiso.slice(0, 10) : "",
        horas_estimadas: dev.horas_estimadas || 0, horas_consumidas: dev.horas_consumidas || 0,
      });
      setChecklist(dev.checklist || {});
      setCorrecciones(dev.correcciones || []);
      setSubtareas(dev.subtareas || []);
    } else {
      setForm(EMPTY);
      setChecklist({});
      setCorrecciones([]);
      setSubtareas([]);
    }
    setDraftCorr("");
    setDraftSub({ titulo: "", descripcion: "" });
    setEditSub(null);
  }, [dev, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const deleteNode = async (payload) => {
    try {
      const { data } = await api.delete("/catalogs/node", { data: payload });
      onCatalogChange?.(data);
      toast.success("Opción eliminada de la lista");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar"); }
  };
  const deleteField = async (tipo, value) => {
    try {
      const { data } = await api.delete("/catalogs/field", { data: { tipo, value } });
      onCatalogChange?.(data);
      toast.success("Opción eliminada de la lista");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar"); }
  };

  const folioRef = useRef("");
  const [folioSaved, setFolioSaved] = useState(false);
  useEffect(() => { folioRef.current = dev?.folio || ""; setFolioSaved(false); }, [dev, open]);

  const autoSaveFolio = async () => {
    if (!isEdit) return;
    const val = (form.folio || "").trim();
    if (val === (folioRef.current || "")) return;
    try {
      await api.put(`/developments/${dev.id}`, { folio: val });
      folioRef.current = val;
      setFolioSaved(true);
      setTimeout(() => setFolioSaved(false), 1600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      ...form,
      responsables: form.responsables || [],
      fecha_solicitud: form.fecha_solicitud ? new Date(form.fecha_solicitud).toISOString() : null,
      fecha_compromiso: form.fecha_compromiso ? new Date(form.fecha_compromiso).toISOString() : null,
      horas_estimadas: Number(form.horas_estimadas), horas_consumidas: Number(form.horas_consumidas),
    };
    try {
      if (isEdit) {
        await api.put(`/developments/${dev.id}`, { ...payload, checklist });
      } else {
        await api.post("/developments", payload);
      }
      toast.success(isEdit ? "Desarrollo actualizado" : "Desarrollo creado");
      // Register catalog entry so systems/modules grow over time
      if (form.sistema && form.sistema.trim()) {
        try {
          const { data } = await api.post("/catalogs", {
            sistema: form.sistema, modulo: form.modulo || null, submodulo: form.submodulo || null,
          });
          onCatalogChange?.(data);
        } catch (err) { console.debug("Registro de catálogo no bloqueante falló:", err); }
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addSub = async () => {
    const titulo = draftSub.titulo.trim();
    if (!titulo) return;
    try {
      const { data } = await api.post(`/developments/${dev.id}/subtareas`, { titulo, descripcion: draftSub.descripcion.trim() });
      setSubtareas(data.subtareas || []);
      setDraftSub({ titulo: "", descripcion: "" });
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleSub = async (sid) => {
    try {
      const { data } = await api.post(`/developments/${dev.id}/subtareas/${sid}/toggle`);
      setSubtareas(data.subtareas || []);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveSubEdit = async (sid) => {
    const titulo = (editSub?.titulo || "").trim();
    if (!titulo) { toast.error("El título no puede estar vacío"); return; }
    try {
      const { data } = await api.put(`/developments/${dev.id}/subtareas/${sid}`, { titulo, descripcion: (editSub.descripcion || "").trim() });
      setSubtareas(data.subtareas || []);
      setEditSub(null);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delSub = async (sid) => {
    try {
      const { data } = await api.delete(`/developments/${dev.id}/subtareas/${sid}`);
      setSubtareas(data.subtareas || []);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addCorr = async () => {
    const texto = draftCorr.trim();
    if (!texto) return;
    try {
      const { data } = await api.post(`/developments/${dev.id}/correcciones`, { texto });
      setCorrecciones(data.correcciones || []);
      if (data.status) setForm((f) => ({ ...f, status: data.status }));
      setDraftCorr("");
      if (data.status === "correccion") toast.info("El desarrollo pasó a estado Corrección");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCorr = async (cid) => {
    try {
      const { data } = await api.put(`/developments/${dev.id}/correcciones/${cid}`);
      setCorrecciones(data.correcciones || []);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delCorr = async (cid) => {
    try {
      const { data } = await api.delete(`/developments/${dev.id}/correcciones/${cid}`);
      setCorrecciones(data.correcciones || []);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dev-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
            {isEdit ? `Editar ${dev.dev_id}` : "Nuevo desarrollo"}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Completa la información del desarrollo, su clasificación y avance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Título</Label>
            <Input data-testid="dev-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
              className="mt-1.5 rounded-sm" placeholder="Nombre del desarrollo" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descripción</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              lang="es" spellCheck={true}
              className="mt-1.5 rounded-sm min-h-[140px]" rows={6} />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Impacto</Label>
            <Textarea data-testid="dev-impacto-input" value={form.impacto} onChange={(e) => set("impacto", e.target.value)}
              lang="es" spellCheck={true} placeholder="Describe el impacto del requerimiento"
              className="mt-1.5 rounded-sm min-h-[90px]" rows={4} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <CatalogField
              label="Sistema"
              testid="dev-sistema-select"
              value={form.sistema}
              options={sistemas.map((s) => s.name)}
              placeholder="Nuevo sistema"
              onChange={(v) => setForm((f) => ({ ...f, sistema: v, modulo: "", submodulo: "" }))}
              onDelete={(v) => deleteNode({ sistema: v })}
            />
            <CatalogField
              label="Módulo"
              testid="dev-modulo-select"
              value={form.modulo}
              options={moduloOptions}
              placeholder="Nuevo módulo"
              disabled={!form.sistema}
              onChange={(v) => setForm((f) => ({ ...f, modulo: v, submodulo: "" }))}
              onDelete={(v) => deleteNode({ sistema: form.sistema, modulo: v })}
            />
            <CatalogField
              label="Submódulo"
              testid="dev-submodulo-select"
              value={form.submodulo}
              options={submoduloOptions}
              placeholder="Nuevo submódulo"
              disabled={!form.modulo}
              onChange={(v) => setForm((f) => ({ ...f, submodulo: v }))}
              onDelete={(v) => deleteNode({ sistema: form.sistema, modulo: form.modulo, submodulo: v })}
            />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Folio de requerimiento</Label>
              {folioSaved && <span className="text-[11px] text-green-600 flex items-center gap-1" data-testid="dev-folio-saved"><Check className="h-3 w-3" />Guardado</span>}
            </div>
            <Input data-testid="dev-folio-input" value={form.folio} onChange={(e) => set("folio", e.target.value)} onBlur={autoSaveFolio}
              placeholder="Ej. REQ-2026-001" className="mt-1.5 rounded-sm" />
            {isEdit && <p className="text-[11px] text-slate-400 mt-1">Se guarda automáticamente al salir del campo.</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CatalogField
              label="Departamento solicitante"
              testid="dev-departamento-select"
              value={form.departamento_solicitante}
              options={departamentoOptions}
              placeholder="Nuevo departamento"
              onChange={(v) => setForm((f) => ({ ...f, departamento_solicitante: v }))}
              onDelete={(v) => deleteField("departamento", v)}
            />
            <CatalogField
              label="Solicitante"
              testid="dev-solicitante-select"
              value={form.solicitante}
              options={solicitanteOptions}
              placeholder="Nombre del solicitante"
              onChange={(v) => setForm((f) => ({ ...f, solicitante: v }))}
              onDelete={(v) => deleteField("solicitante", v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Responsables</Label>
              <div className="mt-1.5 border border-slate-200 rounded-sm max-h-32 overflow-y-auto p-1 space-y-0.5" data-testid="dev-responsables">
                {users.map((u) => {
                  const sel = form.responsables.includes(u.id);
                  return (
                    <div key={u.id} role="button" data-testid={`resp-${u.id}`}
                      onClick={() => setForm((f) => ({ ...f, responsables: sel ? f.responsables.filter((x) => x !== u.id) : [...f.responsables, u.id] }))}
                      className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-sm cursor-pointer transition-colors duration-150 ${sel ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                      <Checkbox checked={sel} className="pointer-events-none" tabIndex={-1} />
                      {u.name}
                    </div>
                  );
                })}
                {users.length === 0 && <p className="text-xs text-slate-400 p-2">Sin usuarios.</p>}
              </div>
              {form.responsables.length > 0 && <p className="text-[11px] text-slate-500 mt-1">{form.responsables.length} responsable(s)</p>}
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de solicitud</Label>
                <Input data-testid="dev-fecha-solicitud-input" type="date" value={form.fecha_solicitud} onChange={(e) => set("fecha_solicitud", e.target.value)}
                  className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha compromiso</Label>
                  {form.fecha_compromiso && (
                    <button type="button" onClick={() => set("fecha_compromiso", "")} className="text-[11px] text-slate-400 hover:text-red-600" data-testid="dev-fecha-clear">Sin fecha</button>
                  )}
                </div>
                <Input data-testid="dev-fecha-input" type="date" value={form.fecha_compromiso} onChange={(e) => set("fecha_compromiso", e.target.value)}
                  className="mt-1.5 rounded-sm" />
                {!form.fecha_compromiso && <p className="text-[11px] text-slate-400 mt-1">Opcional · puede quedar sin fecha</p>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="dev-priority-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_ORDER.map((p) => <SelectItem key={p} value={p}>{PRIORITIES[p].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="dev-status-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUSES[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isEdit && dev?.produccion_at && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-sm px-3 py-2" data-testid="dev-produccion-date">
              <Check className="h-4 w-4 text-green-600 shrink-0" />
              <span className="text-sm text-green-800"><span className="font-semibold">Fecha de producción:</span> {fmtDate(dev.produccion_at)}</span>
              <span className="text-[11px] text-green-600/70 ml-1">(registrada automáticamente)</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Horas estimadas</Label>
              <Input type="number" min={0} value={form.horas_estimadas} onChange={(e) => set("horas_estimadas", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Horas consumidas</Label>
              <Input type="number" min={0} value={form.horas_consumidas} onChange={(e) => set("horas_consumidas", e.target.value)} className="mt-1.5 rounded-sm font-mono" />
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60" data-testid="avance-display">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">% Avance (automático)</Label>
              <span className="font-mono text-sm font-bold text-slate-900" data-testid="dev-avance-value">{subAvance !== null ? subAvance : (dev?.avance ?? 0)}%</span>
            </div>
            <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-slate-900 rounded-full transition-all duration-300" style={{ width: `${subAvance !== null ? subAvance : (dev?.avance ?? 0)}%` }} />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">
              {subtareas.length > 0
                ? `Calculado según subtareas completadas (${completadas} de ${subtareas.length}).`
                : "Sin subtareas: el avance se deriva del estado del desarrollo."}
            </p>
          </div>

          {isEdit ? (
            <div className="border border-slate-200 rounded-sm p-4" data-testid="subtareas-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <ListTodo className="h-4 w-4" /> Subtareas
                <span className="font-mono bg-slate-900 text-white px-1.5 rounded-sm" data-testid="subtareas-count">
                  {completadas} de {subtareas.length} completadas
                </span>
              </Label>
              <p className="text-[11px] text-slate-400 mt-1">Lista de tareas que componen el desarrollo. El avance se calcula automáticamente.</p>

              {canManageSub && (
                <div className="mt-3 space-y-2 border-b border-slate-100 pb-3">
                  <Input data-testid="subtarea-titulo-input" value={draftSub.titulo}
                    onChange={(e) => setDraftSub((d) => ({ ...d, titulo: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                    placeholder="Título de la subtarea" className="rounded-sm h-9" />
                  <div className="flex items-center gap-2">
                    <Input data-testid="subtarea-desc-input" value={draftSub.descripcion}
                      onChange={(e) => setDraftSub((d) => ({ ...d, descripcion: e.target.value }))}
                      placeholder="Descripción (opcional)" className="rounded-sm h-9" />
                    <button type="button" data-testid="add-subtarea-btn" onClick={addSub}
                      className="h-9 px-3 flex items-center gap-1 bg-slate-900 text-white rounded-sm hover:bg-slate-800 transition-colors duration-200 text-sm font-semibold shrink-0">
                      <Plus className="h-4 w-4" /> Agregar
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 space-y-1.5">
                {subtareas.map((s) => (
                  <div key={s.id} data-testid={`subtarea-${s.id}`} className="flex items-start gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
                    <Checkbox className="mt-0.5" checked={!!s.completada} disabled={!canToggleSub}
                      onCheckedChange={() => toggleSub(s.id)} data-testid={`toggle-subtarea-${s.id}`} />
                    {editSub?.id === s.id ? (
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <Input value={editSub.titulo} onChange={(e) => setEditSub((x) => ({ ...x, titulo: e.target.value }))}
                          className="rounded-sm h-8 text-sm" data-testid={`edit-subtarea-titulo-${s.id}`} />
                        <Input value={editSub.descripcion || ""} onChange={(e) => setEditSub((x) => ({ ...x, descripcion: e.target.value }))}
                          placeholder="Descripción" className="rounded-sm h-8 text-sm" />
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${s.completada ? "line-through text-slate-400" : "text-slate-800"}`}>{s.titulo}</div>
                        {s.descripcion && <div className="text-[11px] text-slate-500 mt-0.5">{s.descripcion}</div>}
                        {s.completada && s.completed_by_nombre && <div className="text-[10px] text-slate-400 mt-0.5">Completada por {s.completed_by_nombre}</div>}
                      </div>
                    )}
                    {canManageSub && (
                      <div className="flex items-center gap-1 shrink-0">
                        {editSub?.id === s.id ? (
                          <>
                            <button type="button" onClick={() => saveSubEdit(s.id)} data-testid={`save-subtarea-${s.id}`} className="text-green-600 hover:text-green-700"><Check className="h-4 w-4" /></button>
                            <button type="button" onClick={() => setEditSub(null)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <>
                            <button type="button" onClick={() => setEditSub({ id: s.id, titulo: s.titulo, descripcion: s.descripcion })} data-testid={`edit-subtarea-${s.id}`} className="text-slate-300 hover:text-slate-700 transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => delSub(s.id)} data-testid={`delete-subtarea-${s.id}`} className="text-slate-300 hover:text-red-600 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {subtareas.length === 0 && <p className="text-xs text-slate-400 py-2">Sin subtareas registradas.</p>}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded-sm p-3">
              Guarda el desarrollo para poder agregar subtareas y controlar su avance.
            </p>
          )}

          {isEdit && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Checklist de liberación</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {Object.keys(CHECKLIST_LABELS).map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2 py-1.5 cursor-pointer hover:border-slate-400 transition-colors duration-200">
                    <Checkbox data-testid={`checklist-${k}`} checked={!!checklist[k]} onCheckedChange={(v) => setChecklist((c) => ({ ...c, [k]: !!v }))} />
                    {CHECKLIST_LABELS[k]}
                  </label>
                ))}
              </div>
            </div>
          )}

          {showCorrecciones && (
            <div className="border border-orange-200 bg-orange-50/50 rounded-sm p-4" data-testid="correcciones-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-orange-700 flex items-center gap-1.5">
                <Bug className="h-4 w-4" /> Correcciones a realizar
                {correcciones.filter((c) => c.estado === "pendiente").length > 0 && (
                  <span className="font-mono bg-orange-600 text-white px-1.5 rounded-sm">{correcciones.filter((c) => c.estado === "pendiente").length} pendientes</span>
                )}
              </Label>
              <p className="text-xs text-slate-500 mt-1">Registra los hallazgos de QA que deben corregirse.</p>

              <div className="mt-3 flex items-center gap-2">
                <Input
                  data-testid="correccion-input"
                  value={draftCorr}
                  onChange={(e) => setDraftCorr(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCorr(); } }}
                  placeholder="Describe qué se debe corregir..."
                  className="rounded-sm h-9 bg-white"
                />
                <button type="button" data-testid="add-correccion-btn" onClick={addCorr}
                  className="h-9 px-3 flex items-center gap-1 bg-orange-600 text-white rounded-sm hover:bg-orange-700 transition-colors duration-200 text-sm font-semibold shrink-0">
                  <Plus className="h-4 w-4" /> Agregar
                </button>
              </div>

              <div className="mt-3 space-y-1.5">
                {correcciones.map((c) => (
                  <div key={c.id} data-testid={`correccion-${c.id}`} className="flex items-start gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
                    <Checkbox className="mt-0.5" checked={c.estado === "resuelto"} onCheckedChange={() => toggleCorr(c.id)} data-testid={`toggle-correccion-${c.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${c.estado === "resuelto" ? "line-through text-slate-400" : "text-slate-800"}`}>{c.texto}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">Reportado por {c.reportado_por_nombre}</div>
                    </div>
                    <button type="button" onClick={() => delCorr(c.id)} className="text-slate-300 hover:text-red-600 transition-colors duration-200 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {correcciones.length === 0 && <p className="text-xs text-slate-400 py-2">Sin correcciones registradas.</p>}
              </div>
            </div>
          )}

          {/* Adjuntos */}
          <Attachments dev={dev} isEdit={isEdit} canWrite={user?.role !== "stakeholder"} onSaved={onSaved} />
        </div>

        <DialogFooter>
          {isEdit && (
            <Button variant="outline" data-testid="dev-notify-btn" className="rounded-sm mr-auto border-slate-300" onClick={async () => {
              toast.loading("Enviando correo...", { id: "notify" });
              try {
                const { data } = await api.post(`/developments/${dev.id}/notify`);
                toast.success(`Correo enviado a ${data.recipients.length} destinatario(s)`, { id: "notify" });
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "notify" });
              }
            }}>
              <Mail className="h-4 w-4 mr-1.5" /> Enviar por correo
            </Button>
          )}
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="dev-save-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const fmtBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const isImage = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "").includes("pdf");

function Attachments({ dev, isEdit, canWrite, onSaved }) {
  const [items, setItems] = useState(dev?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => { setItems(dev?.attachments || []); }, [dev]);

  if (!isEdit) {
    return (
      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Paperclip className="h-4 w-4" /> Adjuntos</p>
        <p className="text-xs text-slate-400 mt-2">Guarda el desarrollo para poder adjuntar archivos.</p>
      </div>
    );
  }

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("devflow_token")}` });

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} supera 25 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/developments/${dev.id}/attachments`, { method: "POST", headers: authHeaders(), body: fd });
        if (!res.ok) { toast.error(`No se pudo subir ${file.name}`); continue; }
        const att = await res.json();
        setItems((prev) => [...prev, att]);
      }
      toast.success("Adjunto(s) subido(s)");
      onSaved?.();
    } finally { setUploading(false); }
  };

  const fetchBlob = async (att) => {
    const res = await fetch(`${API}/developments/${dev.id}/attachments/${att.id}`, { headers: authHeaders() });
    if (!res.ok) throw new Error("fetch failed");
    return await res.blob();
  };

  const view = async (att) => {
    setBusy(att.id);
    try {
      const blob = await fetchBlob(att);
      const url = URL.createObjectURL(blob);
      if (isImage(att.content_type) || isPdf(att.content_type)) {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo abrir el adjunto"); }
    finally { setBusy(null); }
  };

  const download = async (att) => {
    setBusy(att.id);
    try {
      const blob = await fetchBlob(att);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No se pudo descargar"); }
    finally { setBusy(null); }
  };

  const remove = async (att) => {
    if (!window.confirm(`¿Eliminar ${att.filename}?`)) return;
    try {
      await api.delete(`/developments/${dev.id}/attachments/${att.id}`);
      setItems((prev) => prev.filter((a) => a.id !== att.id));
      toast.success("Adjunto eliminado");
      onSaved?.();
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="border-t border-slate-100 pt-4" data-testid="dev-attachments">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <Paperclip className="h-4 w-4" /> Adjuntos {items.length > 0 && <span className="font-mono text-slate-400">({items.length})</span>}
        </p>
        {canWrite && (
          <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-sm px-3 h-8 hover:bg-slate-50" data-testid="dev-attach-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir archivos
            <input type="file" multiple className="hidden" disabled={uploading} onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 mt-2">Sin adjuntos.</p>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((att) => (
            <div key={att.id} className="flex items-center gap-2 border border-slate-200 rounded-sm px-3 py-2" data-testid={`dev-attach-${att.id}`}>
              <div className="h-9 w-9 shrink-0 rounded-sm bg-slate-100 flex items-center justify-center text-slate-500">
                {isImage(att.content_type) ? <Eye className="h-4 w-4" /> : <Paperclip className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" title={att.filename}>{att.filename}</p>
                <p className="text-[11px] text-slate-400">{fmtBytes(att.size || 0)} · {att.uploaded_by_name || ""}</p>
              </div>
              <button type="button" onClick={() => view(att)} disabled={busy === att.id} className="text-slate-400 hover:text-slate-800 p-1" title={isImage(att.content_type) || isPdf(att.content_type) ? "Ver" : "Abrir"} data-testid={`dev-attach-view-${att.id}`}>
                {busy === att.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (isImage(att.content_type) || isPdf(att.content_type) ? <Eye className="h-4 w-4" /> : <Download className="h-4 w-4" />)}
              </button>
              <button type="button" onClick={() => download(att)} className="text-slate-400 hover:text-slate-800 p-1" title="Descargar" data-testid={`dev-attach-dl-${att.id}`}><Download className="h-4 w-4" /></button>
              {canWrite && <button type="button" onClick={() => remove(att)} className="text-slate-300 hover:text-red-600 p-1" title="Eliminar" data-testid={`dev-attach-del-${att.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
